// Settings › Onboarding wizard (wave 3, W2_Portal_Settings look): everything
// the mobile wizard reads from /settings in one place: the master switch,
// the "how it works" video, the TIN policy and the per-step table (mode and
// order), with a save bar that counts unsaved changes.
//
// The draft is the FULL /settings object, so the step table's Default choice
// (key removed) works through editSettings, which sends every loaded-but-
// absent key as null (a delete inside its update()). Save writes exactly
// what the old Save wrote: onboardingWizardSince stamped once (the first
// save with the switch on), the video URL trimmed (removed when empty).
// Only with critical edits on.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PlaylistAddCheckRoundedIcon from '@mui/icons-material/PlaylistAddCheckRounded';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import { api } from 'common';
import CircularLoading from '../components/CircularLoading';
import OnboardingStepsTable from '../components/OnboardingStepsTable';
import { showToast, confirmDialog, InlineMessage } from '../components/feedback';
import { tokens as c } from '../theme';
import { formatAddisDateTime } from '../common/exportUtils';
import { SectionCard, SettingRow, SaveBar, IconCircle } from '../components/modern';

// The settings saved here survive switching Settings tabs for the rest of
// the session while the store still holds the settings they started from.
let savedCache = null; // { source, data }

const startFrom = (source) => {
  if (!source) return null;
  if (savedCache && savedCache.source === source) return savedCache.data;
  return { ...source };
};

const keysChanged = (a, b) => {
  if (!a || !b) return [];
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].filter((k) => JSON.stringify(a[k] === undefined ? null : a[k]) !== JSON.stringify(b[k] === undefined ? null : b[k]));
};

// Wide screens (lg and up): the wizard and TIN groups sit side by side in two
// columns; below lg they stack as before. While stacked, each group keeps the
// line under its last row (SettingRow drops it on a last child); in a column
// the group ends clean.
const PAIR_SX = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' },
  columnGap: 4,
  alignItems: 'start',
};
const GROUP_SX = {
  minWidth: 0,
  '& > [id$="-row"]:last-child': { borderBottom: { xs: `1px solid ${c.OUTLINE}`, lg: 0 } },
};

// `pairFirst`: the title at the top of the second column lines up with the
// first title on wide screens.
function GroupTitle({ icon, hue, title, first, pairFirst }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pt: first ? 1.5 : pairFirst ? { xs: 3.5, lg: 1.5 } : 3.5, pb: 0.5 }}>
      <IconCircle icon={icon} hue={hue} size={30} />
      <Typography component="h3" sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.4, color: c.INK }}>
        {title}
      </Typography>
    </Box>
  );
}

const OnboardingSettings = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const { editSettings, clearSettingsViewError } = api;
  const settingsdata = useSelector((state) => state.settingsdata);
  const dispatch = useDispatch();
  const source = settingsdata.settings;

  const [base, setBase] = useState(() => startFrom(source));
  const [data, setData] = useState(() => startFrom(source));
  const [clicked, setClicked] = useState(false);
  const [saving, setSaving] = useState(false);
  const sourceRef = useRef(source);
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (source && source !== sourceRef.current) {
      sourceRef.current = source;
      setBase(startFrom(source));
      setData(startFrom(source));
    }
  }, [source]);

  const changed = useMemo(() => keysChanged(data, base), [data, base]);
  const isChanged = (key) => changed.includes(key);
  const stepsChanged = changed.some((k) => k === 'onboardingStepOrder' || /^onboardingStep[A-Z]/.test(k));

  const setSwitch = (name, checked) => setData((d) => ({ ...d, [name]: checked }));

  const discard = async () => {
    if (!changed.length || saving) return;
    const ok = await confirmDialog({
      title:
        changed.length === 1
          ? t('settings_discard_title_one', { defaultValue: 'Discard 1 unsaved change?' })
          : t('settings_discard_title_many', { n: changed.length, defaultValue: 'Discard {{n}} unsaved changes?' }),
      body: t('settings_discard_body', { defaultValue: 'The settings go back to how they were when you last saved.' }),
      confirmLabel: t('settings_discard', { defaultValue: 'Discard' }),
      destructive: true,
    });
    if (ok && aliveRef.current) setData(base);
  };

  const handleSubmit = async () => {
    if (!data || saving) return;
    if (!settingsdata.settings?.AllowCriticalEditsAdmin) {
      showToast.warning(t('demo_mode'));
      return;
    }
    const payload = { ...data };
    // onboardingWizardSince: stamped once, the first save with the switch on,
    // never reset: accounts created before it are "legacy" (gated by the
    // wizard only while incomplete). editSettings keeps the live stamp if
    // another tab wrote it meanwhile.
    if (payload.onboardingWizardEnabled === true && !payload.onboardingWizardSince) {
      payload.onboardingWizardSince = Date.now();
    }
    const url = String(payload.onboardingHowItWorksVideoURL || '').trim();
    if (url) payload.onboardingHowItWorksVideoURL = url; else delete payload.onboardingHowItWorksVideoURL;
    setClicked(true);
    setSaving(true);
    try {
      await dispatch(editSettings(payload));
      savedCache = { source: sourceRef.current, data: payload };
      if (aliveRef.current) {
        setBase(payload);
        setData(payload);
        showToast.success(t('settings_updated', { defaultValue: 'Settings updated' }));
      }
    } catch (e) {
      if (aliveRef.current) showToast.error(t('update_failed', { defaultValue: 'Update failed' }));
    } finally {
      if (aliveRef.current) setSaving(false);
    }
  };

  const handleClose = () => {
    setClicked(false);
    dispatch(clearSettingsViewError());
  };

  if (!data) return <CircularLoading inline />;

  const wizardOn = data.onboardingWizardEnabled === true;

  const policySwitch = (name, label, help) => (
    <SettingRow
      id={`onb-${name}`}
      layout="switch"
      label={label}
      hint={help || undefined}
      changed={isChanged(name)}
      disabled={!wizardOn}
      control={
        <Switch
          id={`onb-${name}`}
          checked={data[name] === true}
          onChange={(e) => setSwitch(name, e.target.checked)}
          disabled={!wizardOn}
          inputProps={help ? { 'aria-describedby': `onb-${name}-hint` } : undefined}
          sx={{ my: -0.75 }}
        />
      }
    />
  );

  return (
    <Box dir={i18n.dir()}>
      <SectionCard
        id="onboarding"
        icon={<PlaylistAddCheckRoundedIcon />}
        hue="success"
        title={t('onboarding_wizard', { defaultValue: 'Onboarding wizard' })}
        sx={{ display: 'flex', flexDirection: 'column', pb: 0 }}
      >
        <Typography variant="body2" sx={{ color: c.TEXT_3 }}>
          {t('onboarding_settings_help', { defaultValue: 'How new drivers and customers are guided through sign-up in the app, and what a driver must provide. Nothing changes until you Save.' })}
        </Typography>
        {settingsdata.error && settingsdata.error.flag && clicked ? (
          <InlineMessage kind="error" onClose={handleClose} sx={{ mt: 1.5 }}>
            {t('update_failed', { defaultValue: 'Update failed' })}
          </InlineMessage>
        ) : null}

        <Box sx={PAIR_SX}>
          <Box sx={GROUP_SX}>
            <GroupTitle first icon={<PlaylistAddCheckRoundedIcon />} hue="success" title={t('onboarding_wizard_card', { defaultValue: 'Mobile onboarding wizard' })} />
            <SettingRow
              id="onb-onboardingWizardEnabled"
              layout="switch"
              label={t('onboarding_wizard_card_sub', { defaultValue: 'Drivers & customers: new sign-ups and incomplete profiles are guided through their missing steps' })}
              hint={t('onboarding_wizard_enabled_help', { defaultValue: 'When on, the app checks each profile on launch and opens the wizard at the first missing step. Existing drivers who can already go online are never interrupted.' })}
              changed={isChanged('onboardingWizardEnabled')}
              control={
                <Switch
                  id="onb-onboardingWizardEnabled"
                  checked={wizardOn}
                  onChange={(e) => setSwitch('onboardingWizardEnabled', e.target.checked)}
                  inputProps={{ 'aria-describedby': 'onb-onboardingWizardEnabled-hint' }}
                  sx={{ my: -0.75 }}
                />
              }
            />
            {data.onboardingWizardSince ? (
              <Typography variant="body2" sx={{ color: c.TEXT_3, pb: 1 }}>
                {t('onboarding_wizard_since', { defaultValue: 'Wizard first enabled' })}: {formatAddisDateTime(data.onboardingWizardSince)}
              </Typography>
            ) : null}
            <SettingRow
              id="onb-video"
              label={t('onboarding_how_it_works_video_url_short', { defaultValue: '"Watch how it works" video (YouTube URL)' })}
              hint={t('onboarding_how_it_works_video_help', { defaultValue: 'Shown on the last wizard screen. Leave empty to hide the button.' })}
              changed={isChanged('onboardingHowItWorksVideoURL')}
              disabled={!wizardOn}
              control={
                <TextField
                  id="onb-video"
                  size="small"
                  placeholder="https://youtu.be/..."
                  disabled={!wizardOn}
                  value={data.onboardingHowItWorksVideoURL ?? ''}
                  onChange={(e) => setData((d) => ({ ...d, onboardingHowItWorksVideoURL: e.target.value }))}
                  inputProps={{ inputMode: 'url', spellCheck: false, autoComplete: 'off', 'aria-describedby': 'onb-video-hint' }}
                  sx={{ width: { xs: '100%', sm: 420 } }}
                />
              }
            />
          </Box>

          <Box sx={GROUP_SX}>
            <GroupTitle pairFirst icon={<BadgeOutlinedIcon />} hue="info" title={t('onboarding_tin_policy', { defaultValue: 'TIN policy' })} />
            <Typography variant="body2" sx={{ color: c.TEXT_3 }}>
              {t('onboarding_tin_policy_sub', { defaultValue: 'Roll the TIN out in stages: ask in the wizard, remind on the driver home, then require it to go online' })}
            </Typography>
            {policySwitch(
              'onboardingTinStepEnabled',
              t('onboarding_tin_step_enabled_short', { defaultValue: 'Ask for the TIN in the wizard (optional step)' }),
              t('onboarding_tin_step_enabled_help', { defaultValue: 'Default for the TIN row of the step table below; the table can override it.' })
            )}
            {policySwitch('tinPromptEnabled', t('tin_prompt_enabled', { defaultValue: 'Show "Add your TIN" banner to drivers without a TIN' }), null)}
            {policySwitch(
              'tinRequiredForOnline',
              t('tin_required_for_online', { defaultValue: 'Require TIN before a driver can go online' }),
              t('tin_required_for_online_help', { defaultValue: 'Hard gate for every driver, and the wizard step becomes required. Turn on only once most drivers have a TIN.' })
            )}
          </Box>
        </Box>

        <GroupTitle icon={<ListAltOutlinedIcon />} hue="amber" title={t('onboarding_wizard_steps', { defaultValue: 'Wizard steps (drivers)' })} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: c.TEXT_3 }}>
            {t('onboarding_wizard_steps_sub', { defaultValue: 'Mode and order of every step. Default follows the classic switch on General settings. This table is the rule for the wizard, Finish, going online and dispatch.' })}
          </Typography>
        </Box>
        <Box sx={{ pb: 2, ...(stepsChanged ? { borderInlineStart: `3px solid ${c.ACCENT}`, pl: 1.25 } : {}) }}>
          <OnboardingStepsTable data={data} setData={setData} disabled={!wizardOn} t={t} isRTL={isRTL} hideTitle />
        </Box>

        <SaveBar
          changedCount={changed.length}
          onDiscard={discard}
          onSave={handleSubmit}
          saving={saving}
          sx={{ mx: { xs: -2, md: -2.5 }, px: { xs: 2, md: 2.5 } }}
        />
      </SectionCard>
    </Box>
  );
};

export default OnboardingSettings;
